import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSocketConnection } from 'hooks/useSocketConnection';
import { NotificationTypeEnum } from 'modules/notification/constants/notification.enum';
import { NOTIFICATION_COUNT_KEY, NOTIFICATION_USER_KEY } from 'modules/notification/constants/query';

import notificationSound from 'assets/sounds/notification.mp3';

interface NotificationEvent {
  type: NotificationTypeEnum;
  data: any;
  timestamp: string;
}

interface UseNotificationsConfig {
  autoConnect?: boolean;
  soundEnabled?: boolean;
  syncWithReactQuery?: boolean;
  onNotification?: (notification: any) => void;
  onExportDone?: (data: any) => void;
}

interface UseNotificationsReturn {
  isConnected: boolean;
  error: string | null;
  lastNotification: NotificationEvent | null;
  notifications: NotificationEvent[];
  clearNotifications: () => void;
  connect: () => void;
  disconnect: () => void;
}

export const useNotifications = (config?: UseNotificationsConfig): UseNotificationsReturn => {
  const {
    autoConnect = true,
    soundEnabled = true,
    syncWithReactQuery = true,
    onNotification,
    onExportDone,
  } = config || {};

  const queryClient = useQueryClient();

  const { socket, isConnected, error, connect, disconnect } = useSocketConnection({
    autoConnect,
  });

  const [lastNotification, setLastNotification] = useState<NotificationEvent | null>(null);
  const [notifications, setNotifications] = useState<NotificationEvent[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioReadyRef = useRef<boolean>(false);

  useEffect(() => {
    audioRef.current = new Audio(notificationSound);
    audioRef.current.volume = 0.5;

    const prepareAudio = () => {
      const audio = audioRef.current;
      if (audio && !audioReadyRef.current) {
        audio
          .play()
          .then(() => {
            audio.pause();
            audio.currentTime = 0;
            audioReadyRef.current = true;
          })
          .catch(() => {
            // Still blocked, will retry on next interaction
          });
      }
    };

    prepareAudio();

    const events = ['click', 'keydown', 'touchstart'];
    events.forEach((event) => {
      document.addEventListener(event, prepareAudio);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, prepareAudio);
      });
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const playNotificationSound = useCallback(() => {
    if (!soundEnabled) return;

    const audio = audioRef.current;
    if (!audio) return;

    if (!audioReadyRef.current) {
      audio
        .play()
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          audioReadyRef.current = true;
          audio.play().catch((error) => {
            console.warn('Failed to play notification sound after prep:', error);
          });
        })
        .catch((error) => {
          console.warn('Cannot prepare audio yet - user interaction required:', error);
        });
    } else {
      audio.currentTime = 0;
      audio.play().catch((error) => {
        console.warn('Failed to play notification sound:', error);
      });
    }
  }, [soundEnabled]);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setLastNotification(null);
  }, []);

  useEffect(() => {
    if (!socket) return;

    const handleNotification = (data: any) => {
      const event: NotificationEvent = {
        type: NotificationTypeEnum.notification,
        data,
        timestamp: new Date().toISOString(),
      };

      setLastNotification(event);
      setNotifications((prev) => [...prev, event]);
      playNotificationSound();

      if (syncWithReactQuery) {
        queryClient.setQueriesData({ queryKey: [NOTIFICATION_USER_KEY] }, (oldData: any) => {
          if (!oldData?.data) return oldData;

          const exists = oldData.data.some((n: any) => n._id === data._id);
          if (exists) return oldData;

          return {
            ...oldData,
            data: [data, ...oldData.data],
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            total: (oldData?.total || 0) + 1,
          };
        });

        queryClient.setQueriesData({ queryKey: [NOTIFICATION_COUNT_KEY] }, (oldCount: any) => {
          const currentCount = oldCount?.count || oldCount || 0;
          // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
          return { count: currentCount + 1 };
        });
      }

      if (onNotification) {
        onNotification(data);
      }
    };

    const handleExportDone = (data: any) => {
      // console.log('📦 useNotifications - handleExportDone called:', {
      //   data,
      //   hasCallback: !!onExportDone,
      //   timestamp: new Date().toISOString(),
      // });

      // Normalize data structure - WebSocket sends { type, data: { _id, name, ... } }
      const normalizedData = data?.data || data;
      // console.log('📦 Normalized export data:', {
      //   original_id: data?._id,
      //   normalized_id: normalizedData?._id,
      //   normalizedData
      // });

      const event: NotificationEvent = {
        type: NotificationTypeEnum.file_export,
        data: normalizedData,
        timestamp: new Date().toISOString(),
      };

      setLastNotification(event);
      setNotifications((prev) => [...prev, event]);
      // playNotificationSound();

      if (syncWithReactQuery) {
        queryClient.setQueriesData({ queryKey: [NOTIFICATION_USER_KEY] }, (oldData: any) => {
          if (!oldData?.data) return oldData;

          const exists = oldData.data.some((n: any) => n._id === normalizedData._id);
          if (exists) return oldData;

          return {
            ...oldData,
            data: [normalizedData, ...oldData.data],
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            total: (oldData?.total || 0) + 1,
          };
        });

        queryClient.setQueriesData({ queryKey: [NOTIFICATION_COUNT_KEY] }, (oldCount: any) => {
          const currentCount = oldCount?.count || oldCount || 0;
          // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
          return { count: currentCount + 1 };
        });
      }

      if (onExportDone) {
        // console.log('📦 Calling onExportDone callback with normalized data');
        onExportDone(normalizedData);
      } else {
        console.log('⚠️ No onExportDone callback provided');
      }
    };

    // Suscribe to events
    socket.on(NotificationTypeEnum.notification, handleNotification);
    socket.on(NotificationTypeEnum.file_export, handleExportDone);

    // Cleanup: remove listeners
    return () => {
      socket.off(NotificationTypeEnum.notification, handleNotification);
      socket.off(NotificationTypeEnum.file_export, handleExportDone);
    };
  }, [socket, onNotification, onExportDone, syncWithReactQuery, queryClient, playNotificationSound]);

  return {
    isConnected,
    error,
    lastNotification,
    notifications,
    clearNotifications,
    connect,
    disconnect,
  };
};
